/**
 * Anlage: Verstringung, Komponenten, Leitungswege (Kapitel H und I).
 *
 * Die Bedienung der Verstringung ist absichtlich karg: einen String anlegen,
 * dann Module auf der Karte antippen. Das Programm zieht die Linie und prueft
 * die Spannung mit, statt sie dem Planer zu ueberlassen.
 */

import { neueId, speichere } from '../daten/db.ts';
import type { Anlagendaten } from '../anlage.ts';
import type {
  Flaeche,
  Komponente,
  Komponentenart,
  Leitungsweg,
  Projektkomponente,
  Stringzeile,
  Verlegeart,
  Komponentenangaben,
} from '../typen.ts';
import { ARTNAMEN, VERLEGEARTEN } from '../bezeichnungen.ts';
import type { Planung } from '../planung.ts';
import { naechsteFarbe, schlageStringsVor } from '@pv/geometrie';
import { Zahlfeld } from './Zahlfeld.tsx';

interface Eigenschaften {
  projektId: string;
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  anlage: Anlagendaten;
  strings: Stringzeile[];
  projektkomponenten: Projektkomponente[];
  komponenten: Komponente[];
  leitungswege: Leitungsweg[];
  aktiverStringId: string | null;
  onAktiverString: (id: string | null) => void;
  onStringplan: () => void;
}

export function Anlagepanel(e: Eigenschaften): React.JSX.Element {
  const offeneStrings = e.strings.filter((s) => !s.geloescht_am);

  // --- Strings -------------------------------------------------------------
  async function legeStringAn(): Promise<void> {
    const string: Stringzeile = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      name: `String ${offeneStrings.length + 1}`,
      farbe: naechsteFarbe(offeneStrings.length),
      module: [],
      mppt: null,
      wechselrichter_id: e.anlage.wechselrichter?.projektkomponente.id ?? null,
    };
    await speichere('strings', string, {
      projekt_id: string.projekt_id,
      name: string.name,
      farbe: string.farbe,
      module: [],
      wechselrichter_id: string.wechselrichter_id,
    });
    e.onAktiverString(string.id);
  }

  async function aendereString(string: Stringzeile, felder: Partial<Stringzeile>): Promise<void> {
    await speichere('strings', string, felder as Record<string, unknown>);
  }

  async function loescheString(string: Stringzeile, ohneFrage = false): Promise<void> {
    if (!ohneFrage && !confirm(`${string.name} mit ${string.module.length} Modul(en) löschen?`)) return;
    await speichere('strings', string, { geloescht_am: new Date().toISOString() });
    if (e.aktiverStringId === string.id) e.onAktiverString(null);
  }

  /** Vorschlag: reihenweise, gleichmaessig auf die Tracker (PV-H07). */
  async function schlageVor(): Promise<void> {
    const vorhandene = new Map<string, Set<string>>();
    for (const [flaecheId, planung] of e.planungen) {
      vorhandene.set(
        flaecheId,
        new Set(planung.gesetzt.map((platz) => `${platz.reihe}:${platz.spalte}`)),
      );
    }
    const wr = e.anlage.wechselrichter?.stammdaten.kenndaten;
    const maxProString = groessteStringlaenge(e);
    const vorschlag = schlageStringsVor(vorhandene, maxProString, wr?.anzahlMppt ?? 2);

    // Bestehende Strings werden ersetzt — der Vorschlag ist ein Neuanfang.
    // Deshalb nicht ohne Rückfrage: Eine halbe Stunde Verstringen samt
    // Zuleitungen und Namen, an denen die Messwerte hängen, wäre sonst weg.
    if (offeneStrings.length > 0) {
      const module = offeneStrings.reduce((n, s) => n + s.module.length, 0);
      if (
        !confirm(
          `Der Vorschlag ersetzt ${offeneStrings.length} String(s) mit ${module} Modul(en), samt Zuleitungen und Trackern. Ersetzen?`,
        )
      )
        return;
    }
    for (const alt of offeneStrings) await loescheString(alt, true);

    for (let i = 0; i < vorschlag.length; i++) {
      const v = vorschlag[i]!;
      const string: Stringzeile = {
        id: neueId(),
        seq: null,
        schmutzig: true,
        geaendert_am: new Date().toISOString(),
        geloescht_am: null,
        projekt_id: e.projektId,
        name: v.name,
        farbe: v.farbe,
        module: v.module,
        mppt: v.mppt ?? null,
        wechselrichter_id: e.anlage.wechselrichter?.projektkomponente.id ?? null,
      };
      await speichere('strings', string, {
        projekt_id: string.projekt_id,
        name: string.name,
        farbe: string.farbe,
        module: string.module,
        mppt: string.mppt,
        wechselrichter_id: string.wechselrichter_id,
      });
    }
  }

  // --- Komponenten ---------------------------------------------------------
  async function fuegeKomponenteHinzu(komponenteId: string): Promise<void> {
    const pk: Projektkomponente = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      komponente_id: komponenteId,
      anzahl: 1,
      bemerkung: null,
      lat: null,
      lng: null,
    };
    await speichere('projekt_komponenten', pk, {
      projekt_id: pk.projekt_id,
      komponente_id: pk.komponente_id,
      anzahl: pk.anzahl,
    });
  }

  // --- Leitungswege --------------------------------------------------------
  async function legeLeitungAn(von: string, nach: string, art: 'dc' | 'ac'): Promise<void> {
    const leitung: Leitungsweg = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      von,
      nach,
      laenge_m: 0,
      querschnitt_mm2: null,
      artikel_id: null,
      aus_karte: false,
      art,
      verlegeart: 'standard',
      spannung_v: art === 'ac' ? 400 : null,
    };
    await speichere('leitungswege', leitung, {
      projekt_id: leitung.projekt_id,
      von,
      nach,
      laenge_m: 0,
      art,
      verlegeart: 'standard',
      ...(art === 'ac' ? { spannung_v: 400 } : {}),
    });
  }

  const offeneLeitungen = e.leitungswege.filter((l) => !l.geloescht_am);
  const offeneKomponenten = e.projektkomponenten.filter((k) => !k.geloescht_am);

  return (
    <div>
      {/* --- Verstringung ------------------------------------------------- */}
      <h2>Verstringung</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.6rem' }}>
        String anlegen, dann auf der Karte mit dem Finger über die Module wischen — in der
        Reihenfolge, in der das Kabel läuft. Beginnt das Wischen auf einem Modul des Strings, nimmt es
        heraus. Einzelne Module gehen auch mit Antippen.
      </p>

      <div className="knopfreihe">
        <button className="wichtig" onClick={() => void legeStringAn()}>
          String anlegen
        </button>
        <button onClick={() => void schlageVor()} disabled={e.planungen.size === 0}>
          Vorschlag
        </button>
        <button onClick={e.onStringplan} disabled={offeneStrings.length === 0}>
          Stringplan
        </button>
      </div>

      <dl className="zahlenblock">
        <div>
          <dt>Verstringt</dt>
          <dd>
            {e.anlage.befund.moduleVerstringt} von {e.anlage.befund.moduleGesamt}
          </dd>
        </div>
        <div>
          <dt>Strings</dt>
          <dd>{offeneStrings.length}</dd>
        </div>
      </dl>

      {offeneStrings.map((string) => {
        const befund = e.anlage.befund.befunde.get(string.id);
        const aktiv = string.id === e.aktiverStringId;
        return (
          <div
            key={string.id}
            style={{
              borderTop: '1px solid var(--linie)',
              padding: '0.6rem 0',
              background: aktiv ? 'var(--flaeche)' : undefined,
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span
                style={{
                  width: '0.9rem',
                  height: '0.9rem',
                  background: string.farbe,
                  flex: 'none',
                  border: '1px solid var(--schwarz)',
                }}
              />
              <button
                aria-pressed={aktiv}
                onClick={() => e.onAktiverString(aktiv ? null : string.id)}
                style={{ flex: 1, textAlign: 'left' }}
              >
                {string.name} — {string.module.length} Module
              </button>
              <button onClick={() => void loescheString(string)} title="String löschen">
                ×
              </button>
            </div>

            <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
              <label className="klein">
                MPP-Tracker
                <Zahlfeld
                  ganz
                  min={1}
                  max={e.anlage.wechselrichter?.stammdaten.kenndaten.anzahlMppt ?? 6}
                  leerErlaubt
                  wert={string.mppt}
                  onWert={(mppt) => void aendereString(string, { mppt })}
                  style={{ width: '5rem' }}
                />
              </label>
              {befund && (
                <span style={{ fontSize: '0.78rem', alignSelf: 'center' }}>
                  {befund.spannungKaltV.toFixed(0)} V kalt · hoechstens {befund.maxModule}
                </span>
              )}
            </div>

            {(() => {
              const leitung = e.anlage.stringleitungen.get(string.id);
              if (!leitung || string.module.length === 0) return null;
              const m = (wert: number): string =>
                wert.toLocaleString('de-AT', { maximumFractionDigits: 1, useGrouping: false });
              return (
                <div className="stringleitung">
                  <label className="klein">
                    Zuleitung bis Wechselrichter, m
                    <input
                      type="text"
                      inputMode="decimal"
                      key={`${string.id}-${string.zuleitung_m ?? ''}`}
                      defaultValue={string.zuleitung_m ?? ''}
                      placeholder={m(leitung.zuleitungM / 2)}
                      onBlur={(ereignis) => {
                        const text = ereignis.target.value.trim().replace(',', '.');
                        const wert = text === '' ? null : Number(text);
                        if (wert !== null && (!Number.isFinite(wert) || wert < 0)) return;
                        if (wert !== (string.zuleitung_m ?? null)) {
                          void aendereString(string, { zuleitung_m: wert });
                        }
                      }}
                      style={{ width: '6rem' }}
                    />
                  </label>
                  <p className="klein" style={{ margin: 0 }}>
                    <strong>DC-Leitung ≈ {m(leitung.gesamtM)} m</strong> — Zuleitung 2 ×{' '}
                    {m(leitung.zuleitungM / 2)} m, Rückführung {m(leitung.rueckfuehrungM)} m
                    {leitung.verlaengerungen > 0
                      ? `, ${leitung.verlaengerungen} Verlängerung(en) ${m(leitung.verlaengerungenM)} m`
                      : ''}{' '}
                    · {leitung.steckerpaare} Steckerpaare
                  </p>
                </div>
              );
            })()}

            {befund && befund.befund !== 'ok' && (
              <div className="hinweis" style={{ marginTop: '0.4rem' }}>
                {befund.meldung}
              </div>
            )}
          </div>
        );
      })}

      {e.anlage.befund.warnungen
        .filter((w) => !w.stringId)
        .map((warnung, index) => (
          <div key={index} className="hinweis">
            {warnung.text}
          </div>
        ))}

      {/* --- Komponenten -------------------------------------------------- */}
      <h2 style={{ marginTop: '2rem' }}>Komponenten</h2>

      <div className="feld">
        <label htmlFor="komponente">Hinzufügen</label>
        <select
          id="komponente"
          value=""
          onChange={(ereignis) => {
            if (ereignis.target.value) void fuegeKomponenteHinzu(ereignis.target.value);
            ereignis.target.value = '';
          }}
        >
          <option value="">— wählen —</option>
          {(Object.keys(ARTNAMEN) as Komponentenart[]).map((art) => {
            const passende = e.komponenten.filter((k) => k.art === art && k.aktiv);
            if (passende.length === 0) return null;
            return (
              <optgroup key={art} label={ARTNAMEN[art]}>
                {passende.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.hersteller} {k.typ}
                    {k.ist_platzhalter ? ' (Platzhalter)' : ''}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
      </div>

      {offeneKomponenten.map((pk) => {
        const stamm = e.komponenten.find((k) => k.id === pk.komponente_id);
        const angaben: Komponentenangaben = pk.angaben ?? {};
        // Auf den Angaben in der Datenbank aufsetzen: Entfernung eintragen und
        // gleich die Anbindung tippen ließ sonst die Entfernung verschwinden.
        const setzeAngabe = (felder: Partial<Komponentenangaben>): void =>
          void speichere('projekt_komponenten', pk, (aktuell: Projektkomponente) => ({
            angaben: { ...((aktuell.angaben ?? {}) as Komponentenangaben), ...felder },
          }));
        const mitEntfernung =
          stamm?.art === 'smartmeter' || stamm?.art === 'wlv_zk' || stamm?.art === 'wlv_extern';
        return (
          <div key={pk.id} className="komponentenzeile">
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ flex: 1, fontSize: '0.9rem' }}>
                {stamm ? `${stamm.hersteller} ${stamm.typ}` : 'Unbekannt'}
                <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.75rem' }}>
                  {stamm ? ARTNAMEN[stamm.art] : ''}
                  {stamm?.kenndaten.acLeistungKw ? ` · ${stamm.kenndaten.acLeistungKw} kW` : ''}
                  {stamm?.kenndaten.kapazitaetKwh ? ` · ${stamm.kenndaten.kapazitaetKwh} kWh` : ''}
                  {stamm?.kenndaten.anzahlMppt ? ` · ${stamm.kenndaten.anzahlMppt} MPPT` : ''}
                </span>
              </span>
              <Zahlfeld
                ganz
                min={1}
                wert={pk.anzahl}
                aria-label="Anzahl"
                onWert={(anzahl) => anzahl !== null && void speichere('projekt_komponenten', pk, { anzahl })}
                style={{ width: '4.5rem' }}
              />
              <button
                className="schlicht gefaehrlich"
                aria-label="Komponente entfernen"
                onClick={() => {
                  if (!confirm('Komponente aus dem Projekt entfernen?')) return;
                  void speichere('projekt_komponenten', pk, {
                    geloescht_am: new Date().toISOString(),
                  });
                }}
              >
                ×
              </button>
            </div>

            {/* Was je Projekt dazugehoert: wie weit vom Wechselrichter, wie
                angebunden. Daraus kommt die Leitung in der Materialliste. */}
            {(mitEntfernung || stamm?.art === 'internet') && (
              <div className="feldraster" style={{ marginTop: '0.4rem' }}>
                {mitEntfernung && (
                  <label className="klein">
                    Entfernung zum Wechselrichter, m
                    <Zahlfeld
                      min={0}
                      leerErlaubt
                      wert={angaben.entfernungZuWrM}
                      onWert={(wert) => setzeAngabe({ entfernungZuWrM: wert ?? undefined })}
                    />
                  </label>
                )}
                {stamm?.art === 'internet' && (
                  <>
                    <div className="klein">
                      Anbindung
                      <div className="knopfreihe">
                        {(['wlan', 'kabel'] as const).map((wert) => (
                          <button
                            key={wert}
                            aria-pressed={angaben.anbindung === wert}
                            onClick={() => setzeAngabe({ anbindung: wert })}
                          >
                            {wert === 'wlan' ? 'WLAN' : 'Kabel'}
                          </button>
                        ))}
                      </div>
                    </div>
                    {angaben.anbindung === 'kabel' && (
                      <label className="klein">
                        Kabellänge, m
                        <Zahlfeld
                          min={0}
                          leerErlaubt
                          wert={angaben.kabelM}
                          onWert={(wert) => setzeAngabe({ kabelM: wert ? wert : undefined })}
                        />
                      </label>
                    )}
                  </>
                )}
              </div>
            )}
            {stamm && stamm.art !== 'wechselrichter' && stamm.art !== 'speicher' && (
              <input
                className="klein"
                placeholder="Beschreibung, z. B. wo es sitzt"
                defaultValue={angaben.beschreibung ?? ''}
                onBlur={(ereignis) => setzeAngabe({ beschreibung: ereignis.target.value.trim() || undefined })}
                style={{ marginTop: '0.4rem' }}
              />
            )}
          </div>
        );
      })}

      {e.anlage.verhaeltnis && (
        <p style={{ fontSize: '0.85rem', marginTop: '0.8rem' }}>
          Modulleistung zu Wechselrichter: {e.anlage.verhaeltnis.verhaeltnis.toFixed(2)}
          {e.anlage.verhaeltnis.hinweis && (
            <span style={{ display: 'block', color: 'var(--waerme)' }}>
              {e.anlage.verhaeltnis.hinweis}
            </span>
          )}
        </p>
      )}
      {e.anlage.speicherKwh > 0 && (
        <p style={{ fontSize: '0.85rem' }}>Speicher: {e.anlage.speicherKwh} kWh</p>
      )}

      {/* --- Leitungswege ------------------------------------------------- */}
      {/* Der DC-Weg beschreibt den Weg vom Dach zum Wechselrichter: Seine Laenge
          ist die Zuleitung jedes Strings, und das Kabel rechnen die Strings —
          zweimal je String. Hier kommt der Weg selbst dazu: Beschreibung,
          Verlegeart (daraus das Rohr) und der Spannungsabfall. */}
      <h2 style={{ marginTop: '2rem' }}>Leitungswege</h2>
      <div className="knopfreihe">
        <button onClick={() => void legeLeitungAn('PV-Generator', 'Wechselrichter', 'dc')}>
          + DC-Weg
        </button>
        <button onClick={() => void legeLeitungAn('Wechselrichter', 'Zähler', 'ac')}>+ AC-Weg</button>
      </div>

      {offeneLeitungen.map((leitung) => {
        const art = leitung.art ?? (leitung.von === 'PV-Generator' ? 'dc' : 'ac');
        const faelle = e.anlage.spannungsfaelle.get(leitung.id) ?? [];
        return (
          <div key={leitung.id} className="leitungszeile">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong style={{ flex: 1, fontSize: '0.9rem' }}>
                {art === 'dc' ? 'DC-Weg' : 'AC-Weg'} · {leitung.von} bis {leitung.nach}
              </strong>
              <button
                className="schlicht gefaehrlich"
                aria-label="Leitungsweg entfernen"
                onClick={() => {
                  if (!confirm('Leitungsweg entfernen?')) return;
                  void speichere('leitungswege', leitung, {
                    geloescht_am: new Date().toISOString(),
                  });
                }}
              >
                ×
              </button>
            </div>
            <input
              className="klein"
              placeholder="Beschreibung des Wegs, z. B. „über den Dachboden, Kamin entlang in den Keller“"
              defaultValue={leitung.beschreibung ?? ''}
              onBlur={(ereignis) =>
                void speichere('leitungswege', leitung, {
                  beschreibung: ereignis.target.value.trim() || null,
                })
              }
              style={{ margin: '0.3rem 0' }}
            />
            <div className="feldraster">
              <label className="klein">
                Länge, m
                <input
                  type="text"
                  inputMode="decimal"
                  key={`${leitung.id}-l-${leitung.laenge_m}`}
                  defaultValue={leitung.laenge_m || ''}
                  onBlur={(ereignis) => {
                    const wert = Number(ereignis.target.value.replace(',', '.'));
                    if (Number.isFinite(wert) && wert >= 0 && wert !== Number(leitung.laenge_m)) {
                      void speichere('leitungswege', leitung, { laenge_m: wert });
                    }
                  }}
                />
              </label>
              <label className="klein">
                Querschnitt, mm²
                <input
                  type="text"
                  inputMode="decimal"
                  key={`${leitung.id}-q-${leitung.querschnitt_mm2 ?? ''}`}
                  defaultValue={leitung.querschnitt_mm2 ?? ''}
                  placeholder="6"
                  onBlur={(ereignis) => {
                    const text = ereignis.target.value.trim().replace(',', '.');
                    const wert = text === '' ? null : Number(text);
                    // „6 mm²“ getippt war NaN und lokal so gespeichert.
                    if (wert !== null && !(Number.isFinite(wert) && wert > 0)) {
                      ereignis.target.value = String(leitung.querschnitt_mm2 ?? '');
                      return;
                    }
                    if (wert !== (leitung.querschnitt_mm2 ?? null)) {
                      void speichere('leitungswege', leitung, { querschnitt_mm2: wert });
                    }
                  }}
                />
              </label>
              {art === 'ac' && (
                <div className="klein">
                  Spannung
                  <div className="knopfreihe">
                    {[230, 400].map((v) => (
                      <button
                        key={v}
                        aria-pressed={Number(leitung.spannung_v ?? 400) === v}
                        onClick={() => void speichere('leitungswege', leitung, { spannung_v: v })}
                      >
                        {v} V
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="klein" style={{ marginTop: '0.3rem' }}>
              Verlegung
              <div className="knopfreihe">
                {VERLEGEARTEN.filter(([wert]) => art === 'dc' || wert !== 'geschirmt').map(([wert, text]) => (
                  <button
                    key={wert}
                    aria-pressed={(leitung.verlegeart ?? 'standard') === wert}
                    onClick={() => void speichere('leitungswege', leitung, { verlegeart: wert })}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>

            {faelle.length > 0 ? (
              <ul className="spannungsfall">
                {faelle.map((f) => (
                  <li key={f.bezeichnung} className={f.zulaessig ? '' : 'spannungsfall--zu-hoch'}>
                    Spannungsabfall {f.bezeichnung}:{' '}
                    <strong>{f.prozent.toLocaleString('de-AT', { maximumFractionDigits: 2 })} %</strong>
                    {f.zulaessig ? ` — unter ${f.grenzeProzent} %` : ` — über ${f.grenzeProzent} %, größeren Querschnitt nehmen`}
                    {f.annahme ? ` (${f.annahme})` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="klein gedaempft" style={{ margin: '0.3rem 0 0' }}>
                {art === 'dc'
                  ? 'Spannungsabfall je String, sobald verstringt ist und das Modul U_mpp und I_mpp kennt.'
                  : 'Spannungsabfall, sobald ein Wechselrichter mit AC-Leistung gewählt ist.'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Wie lang darf ein String hoechstens werden? Kommt aus der Spannungspruefung,
 * sofern ein Wechselrichter gewaehlt ist — sonst ein neutraler Wert, mit dem
 * der Vorschlag wenigstens etwas liefert.
 */
function groessteStringlaenge(e: Eigenschaften): number {
  const befunde = [...e.anlage.befund.befunde.values()];
  const ausBefund = befunde.find((b) => b.maxModule > 0)?.maxModule;
  if (ausBefund) return ausBefund;

  const wr = e.anlage.wechselrichter?.stammdaten.kenndaten;
  if (!wr?.maxEingangsspannungV) return 18;
  // Grob: 40 V je Modul bei Kaelte. Der genaue Wert faellt danach aus der
  // Pruefung und wird angezeigt.
  return Math.max(4, Math.floor(wr.maxEingangsspannungV / 42));
}
